import Decimal from "decimal.js";
import { minorUnits, UNIT_RATE_DECIMALS, UNIT_RATE_DECIMALS_FINE } from "../currencies/meta";
import { formatDecimalString } from "../i18n/format";
import type { RateSnapshot } from "../rates/fixture";

/**
 * All conversion maths. Never raw JS floats: 0.1 + 0.2 problems become wrong
 * money, and rate arithmetic chains several operations before display.
 *
 * 28 significant digits, comfortably above the 12 the brief requires, so
 * intermediate results never lose precision. Rounding happens exactly once, at
 * display time, in `formatAmount`.
 */
Decimal.set({ precision: 28, rounding: Decimal.ROUND_HALF_UP });

export class MissingRateError extends Error {
  constructor(code: string) {
    super(`No rate for ${code}`);
    this.name = "MissingRateError";
  }
}

function rateOf(snapshot: RateSnapshot, code: string): Decimal {
  const entry = snapshot.rates[code];
  if (!entry) throw new MissingRateError(code);
  return new Decimal(entry.rate);
}

export function hasRate(snapshot: RateSnapshot, code: string): boolean {
  return snapshot.rates[code] !== undefined;
}

/**
 * rate(A -> B) = rates[B] / rates[A], both quoted against the snapshot base.
 * Deriving every pair from one canonical USD table is what keeps the whole
 * grid internally consistent.
 */
export function pairRate(snapshot: RateSnapshot, from: string, to: string): Decimal {
  if (from === to) return new Decimal(1);
  return rateOf(snapshot, to).div(rateOf(snapshot, from));
}

export function convert(
  amount: Decimal.Value,
  from: string,
  to: string,
  snapshot: RateSnapshot,
): Decimal {
  const value = new Decimal(amount);
  if (from === to) return value;
  return value.mul(pairRate(snapshot, from, to));
}

/**
 * Display precision follows ISO 4217 minor units, not a hardcoded 2.
 *
 * The one deliberate exception: an amount below one unit of a high-value
 * currency extends its precision rather than rounding to "0.00", because
 * showing a non-zero quantity as zero is worse than showing an extra digit.
 */
export function formatAmount(value: Decimal, code: string, useGrouping = true): string {
  const base = minorUnits(code);
  let decimals = base;

  if (!value.isZero() && value.abs().lessThan(1)) {
    // Grow precision until at least two significant digits survive, capped so
    // the column cannot run away.
    while (decimals < 8 && value.abs().toFixed(decimals).replace(/[-0.]/g, "") === "") {
      decimals += 1;
    }
    if (decimals > base) decimals = Math.min(decimals + 1, 8);
  }

  return formatDecimalString(value.toFixed(decimals), useGrouping);
}

/**
 * The "1 X = Y" line. Four decimals regardless of minor units, per the brief —
 * dropping to two would hide the difference between currencies that trade
 * close together. Falls to six when four would round the rate to zero.
 */
export function formatUnitRate(snapshot: RateSnapshot, from: string, to: string): string {
  const rate = pairRate(snapshot, from, to);
  const atFour = rate.toFixed(UNIT_RATE_DECIMALS);
  const roundsToZero = new Decimal(atFour).isZero() && !rate.isZero();
  const decimals = roundsToZero ? UNIT_RATE_DECIMALS_FINE : UNIT_RATE_DECIMALS;
  return formatDecimalString(rate.toFixed(decimals));
}

/**
 * Oldest quote date among the given codes.
 *
 * Deliberately the OLDEST and not the newest: a snapshot has no single "as of"
 * date because central banks publish on their own schedules, and reporting the
 * freshest date would present the whole table as newer than it is. Honesty
 * means surfacing the worst case.
 */
export function oldestDate(snapshot: RateSnapshot, codes: readonly string[]): string | null {
  let oldest: string | null = null;
  for (const code of codes) {
    const entry = snapshot.rates[code];
    if (!entry) continue;
    if (oldest === null || entry.date < oldest) oldest = entry.date;
  }
  return oldest;
}

export function newestDate(snapshot: RateSnapshot, codes: readonly string[]): string | null {
  let newest: string | null = null;
  for (const code of codes) {
    const entry = snapshot.rates[code];
    if (!entry) continue;
    if (newest === null || entry.date > newest) newest = entry.date;
  }
  return newest;
}

export function rateDate(snapshot: RateSnapshot, code: string): string | null {
  return snapshot.rates[code]?.date ?? null;
}
